import React, { useState, useRef, useEffect, useCallback } from 'react';
import { es } from 'date-fns/locale';
import { format, subDays, addDays, isSameDay } from 'date-fns';
import { Menu } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../styles/FloatingDateBar.css';
import DatePicker from 'react-datepicker';

const MINIMUM_BLOCK_TIME = 5000; // 5 segundos mínimo

const FloatingDateBar = ({ currentDate, onDateChange, loading, onToggleSidebar }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLocked, setIsLocked] = useState(false);
    const [blockStartTime, setBlockStartTime] = useState(null);
    const datePickerRef = useRef(null);
    const timeoutRef = useRef(null);

    // Controla el bloqueo mínimo de 5 segundos
    useEffect(() => {
        if (loading) {
            // Se inició una carga: activamos bloqueo y guardamos timestamp
            setIsLocked(true);
            setBlockStartTime(Date.now());
            
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        } 
        else if (!loading && blockStartTime) {
            // La carga terminó: calculamos cuánto tiempo llevamos bloqueados
            const elapsed = Date.now() - blockStartTime;
            const remaining = Math.max(0, MINIMUM_BLOCK_TIME - elapsed);
            
            // Si ya pasaron 5 segundos, desbloqueamos inmediatamente
            // Si no, esperamos lo que falte
            timeoutRef.current = setTimeout(() => {
                setIsLocked(false);
                setBlockStartTime(null);
            }, remaining);
        }
        
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [loading, blockStartTime]);

    const isDisabled = loading || isLocked;

    const handlePrevDay = useCallback(() => {
        if (isDisabled) return;
        onDateChange(prev => subDays(prev, 1));
    }, [isDisabled, onDateChange]);

    const handleNextDay = useCallback(() => {
        if (isDisabled) return;
        onDateChange(prev => addDays(prev, 1));
    }, [isDisabled, onDateChange]);

    const handleDateChange = (date) => {
        onDateChange(date);
        setIsOpen(false);
    };

    const toggleCalendar = () => {
        setIsOpen(!isOpen);
    };

    const isToday = isSameDay(currentDate, new Date());

    return (
        <div className="floating-date-bar">
            <button
                className="sidebar-btn"
                onClick={onToggleSidebar}
                aria-label="Abrir menú"
            >
                <Menu size={20} />
            </button>
            
            <div className="date-navigator">
                <button
                    className="nav-btn"
                    onClick={handlePrevDay}
                    disabled={isDisabled}
                    aria-label="Día anterior"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                </button>

                <DatePicker
                    selected={currentDate}
                    onChange={handleDateChange}
                    locale={es}
                    dateFormat="dd/MM/yyyy"
                    open={isOpen}
                    onClickOutside={() => setIsOpen(false)}
                    popperClassName="floating-date-picker"
                    calendarClassName="glass-calendar"
                    popperPlacement="bottom"
                    showPopperArrow={false}
                    customInput={<div style={{ display: 'none' }} />}
                    ref={datePickerRef}
                />
                
                <div className="date-display clickable" onClick={toggleCalendar}>
                    <span className="date-label">
                        {isToday ? 'Hoy' : format(currentDate, 'EEEE', { locale: es })}
                    </span>
                    <span className="date-value">
                        {format(currentDate, 'dd MMM yyyy', { locale: es })}
                    </span>
                </div>

                <button
                    className="nav-btn"
                    onClick={handleNextDay}
                    disabled={isToday || isDisabled}
                    aria-label="Día siguiente"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                </button>
            </div>
            
            {isDisabled && (
                <div className="loading-indicator">
                    {/* Opcional: mostrar cuenta regresiva */}
                </div>
            )}
        </div>
    );
};

export default FloatingDateBar;